import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../components/api';

const useInscription = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  // Données de référence
  const [filieres, setFilieres] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [classes, setClasses] = useState([]);
  const [tarifs, setTarifs] = useState(null);
  const [fraisInscription, setFraisInscription] = useState(0);
  const [tarifsComplementaires, setTarifsComplementaires] = useState([]);
  const [modesPaiement, setModesPaiement] = useState([]);
  const [tranchesPaiement, setTranchesPaiement] = useState([]);

  // États de recherche et suggestions
  const [suggestedParents, setSuggestedParents] = useState([]);
  const [showParentSuggestions, setShowParentSuggestions] = useState(false);

  // Documents
  const [documents, setDocuments] = useState({
    fichier1: null,
    fichier2: null
  });

  // Form data principal
  const [formData, setFormData] = useState({
    // Step 1: Information étudiant
    matricule: '',
    nom: '',
    prenom: '',
    genre: '',
    date_naissance: '',
    lieu_naissance: '',
    nationalite: '',
    contact: '',
    email: '',
    adresse: '',
    filiere: '',
    niveau: '',
    classe: '',

    // Step 2: Information parent/tuteur
    nom_parent: '',
    prenom_parent: '',
    contact_parent: '',
    email_parent: '',
    adresse_parent: '',
    parent_id: '',

    // Step 3: Information paiement
    montant_total: 0,
    montant_paye: 0,
    remise: 0,
    mode_paiement: '',
    frais_inscription_paye: false,
    options_payees: [],
  });

  // Chargement initial des données
  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 Chargement des données d\'inscription...');
      
      const [
        filieresResponse, 
        niveauxResponse, 
        fraisResponse, 
        modalitesResponse,
        modesPaiementResponse
      ] = await Promise.all([
        api.get('/filiere/liste-filieres/'),
        api.get('/niveau/liste-niveaux/'),
        api.get('/etablissement/frais-inscription/'),
        api.get('/niveau/liste-modalites/'),
        api.get('/inscription/liste-modes-paiement/')
      ]);

      // Extraire les données des réponses paginées
      const filieresData = filieresResponse.data.results || filieresResponse.data;
      const niveauxData = niveauxResponse.data.results || niveauxResponse.data;
      const modesPaiementData = modesPaiementResponse.data.data || modesPaiementResponse.data;

      setFilieres(Array.isArray(filieresData) ? filieresData : []);
      setNiveaux(Array.isArray(niveauxData) ? niveauxData : []);
      setFraisInscription(fraisResponse.data.frais || 0);
      setModesPaiement(Array.isArray(modesPaiementData) ? modesPaiementData : []);
      
      // Traiter les modalités de paiement
      const modalitesData = modalitesResponse.data;
      if (Array.isArray(modalitesData)) {
        setTranchesPaiement(modalitesData.sort((a, b) => a.numero_tranche - b.numero_tranche));
      }
      
      console.log('✅ Données d\'inscription chargées avec succès');
    } catch (error) {
      console.error('❌ Erreur lors du chargement des données:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  // Chargement des tarifs complémentaires
  const fetchTarifsComplementaires = useCallback(async () => {
    try {
      const response = await api.get('/niveau/liste-tarifs-complementaires/');
      const tarifsCompData = response.data.results || response.data;
      setTarifsComplementaires(Array.isArray(tarifsCompData) ? tarifsCompData : []);
    } catch (error) {
      console.error('Erreur lors du chargement des tarifs complémentaires:', error);
      toast.error('Erreur lors du chargement des tarifs complémentaires');
    }
  }, []);

  // Chargement des tarifs par niveau/filière
  const fetchTarifs = useCallback(async () => {
    if (!formData.niveau || !formData.filiere) return;
    
    try {
      const response = await api.get(`/niveau/tarifs-niveau/niveau/${formData.niveau}/filiere/${formData.filiere}/`);
      setTarifs(response.data);
      
      setFormData(prev => ({
        ...prev,
        montant_total: response.data.prix,
        montant_paye: 0
      }));
    } catch (error) {
      console.error('Erreur lors du chargement des tarifs:', error);
      toast.error('Erreur lors du chargement des tarifs');
    }
  }, [formData.niveau, formData.filiere]);

  // Chargement des classes par niveau/filière
  const fetchClasses = useCallback(async (niveauId, filiereId) => {
    try {
      const response = await api.get('/classe/classes-by-niveau-filiere/', {
        params: {
          niveau: niveauId,
          filiere: filiereId
        }
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des classes:', error);
      toast.error('Erreur lors du chargement des classes');
    }
  }, []);

  // Chargement des niveaux par filière
  const fetchNiveauxByFiliere = useCallback(async (filiereId) => {
    try {
      const response = await api.get('/niveau/niveaux-by-filiere/', {
        params: { filiere: filiereId }
      });
      setNiveaux(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des niveaux:', error);
      toast.error('Erreur lors du chargement des niveaux');
    }
  }, []);

  // Recherche des parents
  const searchParents = useCallback(async (query) => {
    try {
      if (query.length < 2) {
        setSuggestedParents([]);
        setShowParentSuggestions(false);
        return;
      }

      const response = await api.get('/etudiant/search-parents/', {
        params: { q: query }
      });

      if (response.data && Array.isArray(response.data)) {
        setSuggestedParents(response.data);
        setShowParentSuggestions(true);
      } else {
        setSuggestedParents([]);
        setShowParentSuggestions(false);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche des parents:', error);
      setSuggestedParents([]);
      setShowParentSuggestions(false);
    }
  }, []);

  // Gestion des documents
  const handleDocumentUpload = useCallback((e, field) => {
    const file = e.target.files[0];
    setDocuments(prev => ({
      ...prev,
      [field]: file
    }));
  }, []);

  const handleDocumentRemove = useCallback((field) => {
    setDocuments(prev => ({
      ...prev,
      [field]: null
    }));
  }, []);

  // Gestion des changements de formulaire
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Sélection d'un parent
  const handleParentSelect = useCallback((parent) => {
    setFormData(prev => ({
      ...prev,
      nom_parent: parent.nom_parent || '',
      prenom_parent: parent.prenom_parent || '',
      contact_parent: parent.contact_parent || '',
      email_parent: parent.email_parent || '',
      adresse_parent: parent.adresse_parent || '',
      parent_id: parent.id
    }));
    setShowParentSuggestions(false);
  }, []);

  // Validation des étapes
  const validateStep = useCallback((step) => {
    const newErrors = {};
    if (step === 1) {
      if (!formData.nom) newErrors.nom = 'Le nom est requis';
      if (!formData.prenom) newErrors.prenom = 'Le prénom est requis';
      if (!formData.genre) newErrors.genre = 'Le genre est requis';
      if (!formData.date_naissance) newErrors.date_naissance = 'La date de naissance est requise';
      if (!formData.lieu_naissance) newErrors.lieu_naissance = 'Le lieu de naissance est requis';
      if (!formData.nationalite) newErrors.nationalite = 'La nationalité est requise';
      if (!formData.contact) newErrors.contact = 'Le contact est requis';
      if (!formData.adresse) newErrors.adresse = 'L\'adresse est requise';
      if (!formData.filiere) newErrors.filiere = 'La filière est requise';
      if (!formData.niveau) newErrors.niveau = 'Le niveau est requis';
      if (!formData.classe) newErrors.classe = 'La classe est requise';
    } else if (step === 2) {
      if (!formData.nom_parent) newErrors.nom_parent = 'Le nom du parent est requis';
      if (!formData.prenom_parent) newErrors.prenom_parent = 'Le prénom du parent est requis';
      if (!formData.contact_parent) newErrors.contact_parent = 'Le contact du parent est requis';
      if (!formData.adresse_parent) newErrors.adresse_parent = 'L\'adresse du parent est requise';
    } else if (step === 3) {
      if (!formData.mode_paiement) newErrors.mode_paiement = 'Le mode de paiement est requis';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Navigation entre les étapes
  const nextStep = useCallback(() => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, validateStep]);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => prev - 1);
  }, []);

  // Effects
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    if (formData.niveau && formData.filiere) {
      fetchTarifs();
      fetchTarifsComplementaires();
    }
  }, [formData.niveau, formData.filiere, fetchTarifs, fetchTarifsComplementaires]);

  return {
    // States
    currentStep,
    setCurrentStep,
    loading,
    setLoading,
    error,
    setError,
    errors,
    setErrors,
    formData,
    setFormData,
    documents,
    
    // Données de référence
    filieres,
    niveaux,
    classes,
    tarifs,
    fraisInscription,
    tarifsComplementaires,
    modesPaiement,
    tranchesPaiement,
    
    // Suggestions
    suggestedParents,
    showParentSuggestions,
    setShowParentSuggestions,
    
    // Actions
    handleInputChange,
    handleDocumentUpload,
    handleDocumentRemove,
    handleParentSelect,
    searchParents,
    fetchClasses,
    fetchNiveauxByFiliere,
    validateStep,
    nextStep,
    prevStep,
    
    // Navigation
    navigate
  };
};

export default useInscription; 